import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Registration } from '../types';
import { fetchDashboard, fetchRegistrations, updateRegistration, createRegistration, fetchRegistration, fetchSystemReports, getAgentCommand, getLatestAgentCommand, queueLatestInstallerCommand, fetchInstallerReleaseNotes } from '../services/api';
import { INSTALLER_RELEASE_NOTES, InstallerReleaseNote } from '../data/installerReleaseNotes';
import { useIsOwner } from '../hooks/useIsOwner';
import { useIsAdmin } from '../hooks/useIsAdmin';

type RegistrationRow = Registration & {
    hostName?: string;
    organ?: string;
    vpo?: string;
};

type UpdateRiskReason = {
    code: 'never-online' | 'permission-risk' | 'apikey-risk' | 'version-unsupported';
    message: string;
};

const SESSION_STORAGE_KEY = 'systemReporterVersions';
const CONTENT_VERSION_SESSION_STORAGE_KEY = 'contentVersions';
const LATEST_REPORT_SESSION_STORAGE_KEY = 'latestReportAt';
const NO_VERSION_MARKER = '__NO_VERSION__';
const NO_CONTENT_VERSION_MARKER = '__NO_CONTENT_VERSION__';
const NO_LATEST_REPORT_MARKER = '__NO_LATEST_REPORT__';
const VERSION_RETRY_COOLDOWN_MS = 30 * 1000;
const MIN_AGENT_COMMAND_INSTALLER_VERSION = '3.5.8';
const MIN_DIAGNOSTICS_SYSTEMREPORTER_VERSION = '1.0.73';

const normalizeCustomerPhone = (value: string): string => {
    const trimmed = value.trim();
    return trimmed ? `+${trimmed.replace(/^\+/, '')}` : '';
};

const sanitizeCache = (input: Record<string, string>, emptyMarker: string): Record<string, string> => {
    const normalized: Record<string, string> = {};

    Object.entries(input).forEach(([serial, version]) => {
        normalized[serial] = version || emptyMarker;
    });

    return normalized;
};

const getDisplayVersion = (value?: string): string => {
    if (!value || value === NO_VERSION_MARKER) {
        return '';
    }

    return value;
};

const getDisplayContentVersion = (value?: string): string => {
    if (!value || value === NO_CONTENT_VERSION_MARKER) {
        return '';
    }

    return value;
};

const parseDateSafe = (value?: string): number => {
    if (!value) {
        return 0;
    }

    const time = Date.parse(value);
    return Number.isNaN(time) ? 0 : time;
};

// ContentAgent polls every 60s and the backend persists at most every 2 minutes,
// so 5 minutes tolerates a missed poll without flapping.
const AGENT_ONLINE_THRESHOLD_MS = 5 * 60 * 1000;

type PresenceInfo = { lastSeenAt?: string; agentLastSeenAt?: string };

const isOnlineNow = (registration: PresenceInfo): boolean => {
    const agentSeenMs = parseDateSafe(registration.agentLastSeenAt);
    return agentSeenMs > 0 && Date.now() - agentSeenMs <= AGENT_ONLINE_THRESHOLD_MS;
};

// InformationalVersion may carry a "+<commit>" suffix; show only the semantic part.
const formatAgentVersion = (agentVersion?: string): string => agentVersion?.split('+')[0]?.trim() ?? '';

const getEffectiveLastSeenAt = (registration: PresenceInfo): string | undefined => {
    const heartbeatMs = parseDateSafe(registration.lastSeenAt);
    const agentMs = parseDateSafe(registration.agentLastSeenAt);
    if (agentMs === 0 && heartbeatMs === 0) {
        return registration.lastSeenAt ?? registration.agentLastSeenAt;
    }
    return agentMs > heartbeatMs ? registration.agentLastSeenAt : registration.lastSeenAt;
};

const formatLastSeenLabel = (lastSeenAt?: string): string => {
    if (!lastSeenAt) {
        return 'Never';
    }

    const timestamp = parseDateSafe(lastSeenAt);
    if (!timestamp) {
        return lastSeenAt;
    }

    const diffMs = Date.now() - timestamp;
    const minutes = Math.floor(diffMs / (60 * 1000));
    if (minutes < 1) {
        return 'Just now';
    }
    if (minutes < 60) {
        return `${minutes}m ago`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return `${hours}h ago`;
    }

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};

const formatDateTimeLabel = (value?: string): string => {
    if (!value) {
        return '-';
    }

    const timestamp = parseDateSafe(value);
    if (!timestamp) {
        return value;
    }

    const dateText = new Date(timestamp).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });

    return dateText;
};

const normalizeTeamViewerId = (value?: string): string => {
    if (!value) {
        return '';
    }

    return value.replace(/\D/g, '');
};

const parseVersion = (value?: string): number[] => {
    if (!value) {
        return [];
    }

    return value
        .split(/[^0-9]+/)
        .filter(Boolean)
        .map((part) => Number.parseInt(part, 10))
        .filter((part) => Number.isFinite(part));
};

const isVersionLowerThan = (currentVersion: string | undefined, minimumVersion: string): boolean => {
    const current = parseVersion(currentVersion);
    const minimum = parseVersion(minimumVersion);
    const maxLength = Math.max(current.length, minimum.length);

    for (let index = 0; index < maxLength; index += 1) {
        const currentPart = current[index] ?? 0;
        const minimumPart = minimum[index] ?? 0;

        if (currentPart < minimumPart) {
            return true;
        }

        if (currentPart > minimumPart) {
            return false;
        }
    }

    return false;
};

const isValidSemanticVersion = (version: string): boolean => {
    // Check if version matches semantic versioning pattern: X.Y.Z or X.Y.Z.W, etc.
    const semanticVersionPattern = /^\d+\.\d+\.\d+(\.\d+)*$/;
    return semanticVersionPattern.test(version);
};

const getUpdateRiskReasons = (lastSeenAt?: string, systemReporterVersion?: string): UpdateRiskReason[] => {
    const reasons: UpdateRiskReason[] = [];
    const lastSeenMs = parseDateSafe(lastSeenAt);
    const oneWeekMs = 7 * 24 * 60 * 60 * 1000;

    if (lastSeenMs === 0 || Date.now() - lastSeenMs > oneWeekMs) {
        reasons.push({
            code: 'never-online',
            message: 'PC is not coming online (never seen online or offline for more than 1 week).',
        });
    }

    if (systemReporterVersion && isVersionLowerThan(systemReporterVersion, '1.0.54')) {
        reasons.push({
            code: 'permission-risk',
            message: 'SystemReporter version is lower than 1.0.54: possible folder permission issue that can block self-update.',
        });
    }

    if (systemReporterVersion && isVersionLowerThan(systemReporterVersion, '1.0.48')) {
        reasons.push({
            code: 'apikey-risk',
            message: 'SystemReporter version is lower than 1.0.48: API key has changed, so self-update no longer works.',
        });
    }

    if (!systemReporterVersion || !isValidSemanticVersion(systemReporterVersion)) {
        reasons.push({
            code: 'version-unsupported',
            message: 'Software version is unknown or unsupported. Self-update is not possible.',
        });
    }

    return reasons;
};

const Registrations: React.FC = () => {
    const isOwner = useIsOwner();
    const isAdmin = useIsAdmin();
    const location = useLocation();
    const navigate = useNavigate();
    const [registrations, setRegistrations] = useState<RegistrationRow[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [showImportedWithoutReports, setShowImportedWithoutReports] = useState<boolean>(false);
    const [activeSerialNumber, setActiveSerialNumber] = useState<string | null>(null);

    const [selectedRegistration, setSelectedRegistration] = useState<RegistrationRow | null>(null);
    const [customerPhoneDraft, setCustomerPhoneDraft] = useState<string>('');
    const [customerEmailDraft, setCustomerEmailDraft] = useState<string>('');
    const [notesDraft, setNotesDraft] = useState<string>('');
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(false);
    const [teamViewerIdsBySerial, setTeamViewerIdsBySerial] = useState<Record<string, string>>({});
    const [loadingTeamViewerForSerial, setLoadingTeamViewerForSerial] = useState<string | null>(null);
    const [teamViewerErrors, setTeamViewerErrors] = useState<Record<string, string>>({});
    const [latestReportErrors, setLatestReportErrors] = useState<Record<string, string>>({});
    const [systemReporterVersionBySerial, setSystemReporterVersionBySerial] = useState<Record<string, string>>({});
    const [contentVersionBySerial, setContentVersionBySerial] = useState<Record<string, string>>({});
    const [latestReportAtBySerial, setLatestReportAtBySerial] = useState<Record<string, string>>({});
    const [isLoadingVersionsBatch, setIsLoadingVersionsBatch] = useState<boolean>(false);
    const [loadingLatestReportForSerial, setLoadingLatestReportForSerial] = useState<string | null>(null);
    const [agentCommandLoadingForSerial, setAgentCommandLoadingForSerial] = useState<string | null>(null);
    const [agentCommandMessages, setAgentCommandMessages] = useState<Record<string, string>>({});
    const [agentCommandErrors, setAgentCommandErrors] = useState<Record<string, string>>({});
    const [agentCommandStatuses, setAgentCommandStatuses] = useState<Record<string, {
        status: string;
        exitCode?: number;
        output?: string;
        completedAtUtc?: string;
    }>>({});
    const [installerReleaseNotes, setInstallerReleaseNotes] = useState<InstallerReleaseNote[]>(INSTALLER_RELEASE_NOTES);
    const [installerUpdateConfirmationSerial, setInstallerUpdateConfirmationSerial] = useState<string | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
    const [createSerialDraft, setCreateSerialDraft] = useState<string>('');
    const [createCustomerDraft, setCreateCustomerDraft] = useState<string>('');
    const [createPhoneDraft, setCreatePhoneDraft] = useState<string>('');
    const [createEmailDraft, setCreateEmailDraft] = useState<string>('');
    const [createNotesDraft, setCreateNotesDraft] = useState<string>('');
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const agentCommandPollIntervalsRef = useRef<Record<string, NodeJS.Timeout>>({});
    const [expandedSerials, setExpandedSerials] = useState<Set<string>>(new Set());
    const loadingVersionSerialsRef = useRef<Set<string>>(new Set());
    const versionRetryAfterBySerialRef = useRef<Record<string, number>>({});

    const closeDeviceDetails = () => {
        setInstallerUpdateConfirmationSerial(null);
        setActiveSerialNumber(null);
    };

    const mergeRegistrationsWithDashboard = async (): Promise<RegistrationRow[]> => {
        const [registrationData, dashboardData] = await Promise.all([
            fetchRegistrations(),
            fetchDashboard(),
        ]);

        const dashboardBySerial = new Map(
            dashboardData.map((item) => [item.serialNumber, item])
        );

        const merged = registrationData.map((registration) => {
            const dashboardItem = dashboardBySerial.get(registration.serialNumber);
            return {
                ...registration,
                customer: dashboardItem?.customer || registration.customer || '-',
                hostName: dashboardItem?.hostName,
                organ: dashboardItem?.organ,
                vpo: dashboardItem?.vpo,
            };
        });

        merged.sort((a, b) => {
            const aTime = parseDateSafe(getEffectiveLastSeenAt(a)) || parseDateSafe(a.registeredAt);
            const bTime = parseDateSafe(getEffectiveLastSeenAt(b)) || parseDateSafe(b.registeredAt);
            return bTime - aTime;
        });

        return merged;
    };

    const loadVersionsInBatches = async (registrationsToLoad: RegistrationRow[]): Promise<string[]> => {
        setIsLoadingVersionsBatch(true);
        const batchSize = 5;
        const failedSerialNumbers: string[] = [];

        try {
            // Load from sessionStorage first
            const cached = sessionStorage.getItem(SESSION_STORAGE_KEY);
            const cachedContentVersions = sessionStorage.getItem(CONTENT_VERSION_SESSION_STORAGE_KEY);
            const cachedLatestReports = sessionStorage.getItem(LATEST_REPORT_SESSION_STORAGE_KEY);
            const versionResults: Record<string, string> = cached
                ? sanitizeCache(JSON.parse(cached) as Record<string, string>, NO_VERSION_MARKER)
                : {};
            const contentVersionResults: Record<string, string> = cachedContentVersions
                ? sanitizeCache(JSON.parse(cachedContentVersions) as Record<string, string>, NO_CONTENT_VERSION_MARKER)
                : {};
            const latestReportResults: Record<string, string> = cachedLatestReports
                ? sanitizeCache(JSON.parse(cachedLatestReports) as Record<string, string>, NO_LATEST_REPORT_MARKER)
                : {};
            
            if (cached) {
                setSystemReporterVersionBySerial(versionResults);
            }

            if (cachedContentVersions) {
                setContentVersionBySerial(contentVersionResults);
            }

            if (cachedLatestReports) {
                setLatestReportAtBySerial(latestReportResults);
            }

            // Find which ones still need loading
            const needsLoading = registrationsToLoad.filter(
                (reg) => !versionResults[reg.serialNumber]
                    || !contentVersionResults[reg.serialNumber]
                    || !latestReportResults[reg.serialNumber]
            );

            if (needsLoading.length === 0) {
                setIsLoadingVersionsBatch(false);
                return [];
            }

            // Fetch with limited concurrency to avoid browser/network resource exhaustion.
            for (let i = 0; i < needsLoading.length; i += batchSize) {
                const batch = needsLoading.slice(i, i + batchSize);
                const batchResults = await Promise.allSettled(
                    batch.map(async (registration) => {
                        const reports = await fetchSystemReports(registration.serialNumber, 1);
                        const latestReport = reports[0] as (Record<string, unknown> | undefined);
                        const rawVersion = latestReport?.systemReporterVersion;
                        const rawContentVersion = latestReport?.contentVersion;
                        const rawLatestReportAt = latestReport?.timestamp;
                        const version = typeof rawVersion === 'string' && isValidSemanticVersion(rawVersion) ? rawVersion : '';
                        const contentVersion = typeof rawContentVersion === 'string' ? rawContentVersion.trim() : '';
                        const latestReportAt = typeof rawLatestReportAt === 'string' ? rawLatestReportAt.trim() : '';

                        return {
                            serialNumber: registration.serialNumber,
                            version,
                            contentVersion,
                            latestReportAt,
                        };
                    })
                );

                batchResults.forEach((result, index) => {
                    if (result.status === 'fulfilled') {
                        versionResults[result.value.serialNumber] = result.value.version || NO_VERSION_MARKER;
                        contentVersionResults[result.value.serialNumber] = result.value.contentVersion || NO_CONTENT_VERSION_MARKER;
                        latestReportResults[result.value.serialNumber] = result.value.latestReportAt || NO_LATEST_REPORT_MARKER;
                    } else {
                        const serialNumber = batch[index]?.serialNumber;
                        if (serialNumber) {
                            failedSerialNumbers.push(serialNumber);
                        }
                    }
                });

                // Update UI after each batch
                setSystemReporterVersionBySerial({ ...versionResults });
                setContentVersionBySerial({ ...contentVersionResults });
                setLatestReportAtBySerial({ ...latestReportResults });
                sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(versionResults));
                sessionStorage.setItem(CONTENT_VERSION_SESSION_STORAGE_KEY, JSON.stringify(contentVersionResults));
                sessionStorage.setItem(LATEST_REPORT_SESSION_STORAGE_KEY, JSON.stringify(latestReportResults));
            }
        } catch (err) {
            console.error('Error loading versions batch:', err);
            return registrationsToLoad.map((item) => item.serialNumber);
        } finally {
            setIsLoadingVersionsBatch(false);
        }

        return failedSerialNumbers;
    };

    useEffect(() => {
        const getRegistrations = async () => {
            try {
                const merged = await mergeRegistrationsWithDashboard();

                setRegistrations(merged);
                setActiveSerialNumber((previous) => previous ?? null);
                void loadVersionsInBatches(merged.filter((item) => parseDateSafe(item.firstReportAt) > 0));

                // Deep link from other pages (e.g. Tickets) via ?serialNumber=...
                const params = new URLSearchParams(location.search);
                const deepLinkSerial = params.get('serialNumber')?.trim();
                if (deepLinkSerial && merged.some((item) => item.serialNumber === deepLinkSerial)) {
                    // Device may have no reports yet, which the default filter hides;
                    // show it anyway so the modal isn't immediately closed again below.
                    setShowImportedWithoutReports(true);
                    setActiveSerialNumber(deepLinkSerial);
                    params.delete('serialNumber');
                    navigate({ search: params.toString() }, { replace: true });
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to fetch registrations';
                setError(message);
            } finally {
                setLoading(false);
            }
        };

        getRegistrations();

        // Cleanup polling intervals on unmount
        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            const intervals = agentCommandPollIntervalsRef.current;
            Object.values(intervals).forEach((interval) => {
                clearInterval(interval);
            });
        };
        // Runs once on mount only; location.search/navigate intentionally excluded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openRegistrationDetails = async (registration: RegistrationRow) => {
        setIsLoadingDetails(true);
        setSaveError(null);
        try {
            // Fetch the latest data for this registration
            const updated = await fetchRegistration(registration.serialNumber);
            setSelectedRegistration({ ...registration, ...updated });
            setCustomerPhoneDraft(updated.customerPhone ?? '');
            setCustomerEmailDraft(updated.customerEmail ?? '');
            setNotesDraft(updated.notes ?? '');
        } catch (err) {
            // If fetch fails, use the current data
            setSelectedRegistration(registration);
            setCustomerPhoneDraft(registration.customerPhone ?? '');
            setCustomerEmailDraft(registration.customerEmail ?? '');
            setNotesDraft(registration.notes ?? '');
        } finally {
            setIsLoadingDetails(false);
        }
    };

    const closeRegistrationDetails = () => {
        setSelectedRegistration(null);
        setCustomerPhoneDraft('');
        setCustomerEmailDraft('');
        setNotesDraft('');
        setSaveError(null);
    };

    const refreshRegistrationDetails = async () => {
        if (!selectedRegistration) return;
        setIsLoadingDetails(true);
        try {
            const updated = await fetchRegistration(selectedRegistration.serialNumber);
            setSelectedRegistration({ ...selectedRegistration, ...updated });
            setCustomerPhoneDraft(updated.customerPhone ?? '');
            setCustomerEmailDraft(updated.customerEmail ?? '');
            setNotesDraft(updated.notes ?? '');
            setSaveError(null);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to refresh data';
            setSaveError(message);
        } finally {
            setIsLoadingDetails(false);
        }
    };

    const saveRegistration = async () => {
        if (!selectedRegistration) {
            return;
        }

        setIsSaving(true);
        setSaveError(null);

        try {
            const updated = await updateRegistration(selectedRegistration.serialNumber, {
                customerPhone: normalizeCustomerPhone(customerPhoneDraft),
                customerEmail: customerEmailDraft.trim(),
                notes: notesDraft,
            });

            setRegistrations((previous) =>
                previous.map((item) =>
                    item.serialNumber === updated.serialNumber
                        ? { ...item, ...updated }
                        : item
                )
            );

            closeRegistrationDetails();
        } catch (err) {
            console.error('Save error:', err);
            const message = err instanceof Error ? err.message : 'Failed to save notes';
            console.log('Setting error message:', message);
            setSaveError(message);
        } finally {
            setIsSaving(false);
        }
    };

    const openCreateRegistration = () => {
        setCreateSerialDraft('');
        setCreateCustomerDraft('');
        setCreatePhoneDraft('');
        setCreateEmailDraft('');
        setCreateNotesDraft('');
        setCreateError(null);
        setIsCreateModalOpen(true);
    };

    const closeCreateRegistration = () => {
        setIsCreateModalOpen(false);
        setCreateError(null);
    };

    const submitCreateRegistration = async () => {
        const serialNumber = createSerialDraft.trim();
        const customer = createCustomerDraft.trim();

        if (!serialNumber || !customer) {
            setCreateError('Serial number and customer are required.');
            return;
        }

        setIsCreating(true);
        setCreateError(null);

        try {
            const created = await createRegistration({
                serialNumber,
                customer,
                customerPhone: createPhoneDraft.trim() || undefined,
                customerEmail: createEmailDraft.trim() || undefined,
                notes: createNotesDraft.trim() || undefined,
            });

            setRegistrations((previous) => [created, ...previous]);
            setShowImportedWithoutReports(true);
            setIsCreateModalOpen(false);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to create registration';
            setCreateError(message);
        } finally {
            setIsCreating(false);
        }
    };

    const loadTeamViewerForRegistration = useCallback(async (registration: RegistrationRow) => {
        const serialNumber = registration.serialNumber;

        if (loadingTeamViewerForSerial === serialNumber) {
            return;
        }

        setLoadingTeamViewerForSerial(serialNumber);
        setTeamViewerErrors((previous) => ({ ...previous, [serialNumber]: '' }));

        try {
            let teamViewerId = normalizeTeamViewerId(registration.teamViewerId)
                || teamViewerIdsBySerial[serialNumber]
                || '';

            if (!teamViewerId) {
                const reports = await fetchSystemReports(serialNumber, 1);
                teamViewerId = normalizeTeamViewerId(reports[0]?.teamViewerId);
            }

            if (!teamViewerId) {
                throw new Error('No TeamViewer ID available for this registration.');
            }

            setTeamViewerIdsBySerial((previous) => ({ ...previous, [serialNumber]: teamViewerId }));
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to open TeamViewer';
            setTeamViewerErrors((previous) => ({ ...previous, [serialNumber]: message }));
        } finally {
            setLoadingTeamViewerForSerial(null);
        }
    }, [loadingTeamViewerForSerial, teamViewerIdsBySerial]);

    const loadDeviceDetailsForRegistration = useCallback(async (registration: RegistrationRow) => {
        const serialNumber = registration.serialNumber;
        const hasReport = parseDateSafe(registration.firstReportAt) > 0;

        if (!hasReport) {
            return;
        }

        const missingVersion = !systemReporterVersionBySerial[serialNumber];
        const missingTeamViewer = !normalizeTeamViewerId(registration.teamViewerId)
            && !teamViewerIdsBySerial[serialNumber];

        if (missingVersion) {
            const now = Date.now();
            const retryAfter = versionRetryAfterBySerialRef.current[serialNumber] ?? 0;

            if (!loadingVersionSerialsRef.current.has(serialNumber) && now >= retryAfter) {
                loadingVersionSerialsRef.current.add(serialNumber);
                try {
                    const failedSerialNumbers = await loadVersionsInBatches([registration]);
                    if (failedSerialNumbers.includes(serialNumber)) {
                        versionRetryAfterBySerialRef.current[serialNumber] = Date.now() + VERSION_RETRY_COOLDOWN_MS;
                    }
                } finally {
                    loadingVersionSerialsRef.current.delete(serialNumber);
                }
            }
        }

        if (missingTeamViewer) {
            await loadTeamViewerForRegistration(registration);
        }
    }, [systemReporterVersionBySerial, teamViewerIdsBySerial, loadTeamViewerForRegistration]);

    const openLatestReportForRegistration = async (registration: RegistrationRow) => {
        const serialNumber = registration.serialNumber;
        setLoadingLatestReportForSerial(serialNumber);
        setLatestReportErrors((previous) => ({ ...previous, [serialNumber]: '' }));

        try {
            const reportUrl = `https://report.gjschipper.nl/report/${encodeURIComponent(serialNumber)}`;
            const link = document.createElement('a');
            link.href = reportUrl;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.click();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to open reports';
            setLatestReportErrors((previous) => ({ ...previous, [serialNumber]: message }));
        } finally {
            setLoadingLatestReportForSerial(null);
        }
    };

    const triggerInstallerUpdateCommand = async (registration: RegistrationRow) => {
        const serialNumber = registration.serialNumber;
        setAgentCommandLoadingForSerial(serialNumber);
        setAgentCommandErrors((previous) => ({ ...previous, [serialNumber]: '' }));
        setAgentCommandMessages((previous) => ({ ...previous, [serialNumber]: '' }));
        setAgentCommandStatuses((previous) => {
            const next = { ...previous };
            delete next[serialNumber];
            return next;
        });

        try {
            const command = await queueLatestInstallerCommand(serialNumber);

            setAgentCommandMessages((previous) => ({
                ...previous,
                [serialNumber]: `Installer queued (${command.id})`,
            }));
            pollAgentCommandStatus(serialNumber, command.id);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to queue installer command';
            setAgentCommandErrors((previous) => ({ ...previous, [serialNumber]: message }));
        } finally {
            setAgentCommandLoadingForSerial(null);
        }
    };

    const openInstallerUpdateConfirmation = (serialNumber: string) => {
        setInstallerUpdateConfirmationSerial(serialNumber);
    };

    const closeInstallerUpdateConfirmation = () => {
        setInstallerUpdateConfirmationSerial(null);
    };

    const confirmInstallerUpdateCommand = async (registration: RegistrationRow) => {
        setInstallerUpdateConfirmationSerial(null);
        await triggerInstallerUpdateCommand(registration);
    };

    const pollAgentCommandStatus = useCallback((serialNumber: string, commandId: string) => {
        // Clear existing interval if any
        if (agentCommandPollIntervalsRef.current[serialNumber]) {
            clearInterval(agentCommandPollIntervalsRef.current[serialNumber]);
        }

        // Start polling
        const pollInterval = setInterval(async () => {
            try {
                const commandData = await getAgentCommand(serialNumber, commandId);
                setAgentCommandStatuses((previous) => ({
                    ...previous,
                    [serialNumber]: {
                        status: commandData.status,
                        exitCode: commandData.exitCode,
                        output: commandData.output,
                        completedAtUtc: commandData.completedAtUtc,
                    },
                }));

                // Stop polling if command is completed
                if (commandData.status === 'succeeded' || commandData.status === 'failed') {
                    clearInterval(pollInterval);
                    delete agentCommandPollIntervalsRef.current[serialNumber];
                }
            } catch (err) {
                console.error('Error polling agent command:', err);
                // Continue polling on error
            }
        }, 2000); // Poll every 2 seconds

        agentCommandPollIntervalsRef.current[serialNumber] = pollInterval;
    }, []);

    const hasNoTeamViewerIdError = (serialNumber: string): boolean => {
        const message = teamViewerErrors[serialNumber] ?? '';
        return message.toLowerCase().includes('no teamviewer id available');
    };


    const loadAllDeviceData = async () => {
        setIsLoadingVersionsBatch(true);
        try {
            const merged = await mergeRegistrationsWithDashboard();

            setRegistrations(merged);
            setActiveSerialNumber((previous) => {
                if (!previous) {
                    return null;
                }

                return merged.some((item) => item.serialNumber === previous)
                    ? previous
                    : null;
            });

            // Clear and reload version cache
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            sessionStorage.removeItem(CONTENT_VERSION_SESSION_STORAGE_KEY);
            sessionStorage.removeItem(LATEST_REPORT_SESSION_STORAGE_KEY);
            setSystemReporterVersionBySerial({});
            setContentVersionBySerial({});
            setLatestReportAtBySerial({});
            versionRetryAfterBySerialRef.current = {};
            void loadVersionsInBatches(merged.filter((item) => parseDateSafe(item.firstReportAt) > 0));
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to refresh device data';
            console.error('Error refreshing device data:', err);
            setError(message);
        } finally {
            setIsLoadingVersionsBatch(false);
        }
    };

    const filteredRegistrations = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        const withReport = registrations.filter((item) => parseDateSafe(item.firstReportAt) > 0);
        const source = query || showImportedWithoutReports ? registrations : withReport;

        if (!query) {
            return source;
        }

        return source.filter((item) => {
            const haystack = [
                item.serialNumber,
                item.customer,
                item.customerPhone,
                item.hostName,
                item.clientCity,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return haystack.includes(query);
        });
    }, [registrations, searchTerm, showImportedWithoutReports]);

    const activeRegistration = useMemo(() => {
        if (!activeSerialNumber) {
            return null;
        }

        return registrations.find((item) => item.serialNumber === activeSerialNumber) ?? null;
    }, [registrations, activeSerialNumber]);

    useEffect(() => {
        let isMounted = true;

        void fetchInstallerReleaseNotes()
            .then((notes) => {
                if (isMounted) {
                    setInstallerReleaseNotes(notes);
                }
            })
            .catch((error) => {
                console.warn('Falling back to bundled installer release notes:', error);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!activeRegistration) {
            return;
        }

        const serialNumber = activeRegistration.serialNumber;
        void getLatestAgentCommand(serialNumber)
            .then((commandData) => {
                if (!commandData) {
                    setAgentCommandStatuses((previous) => {
                        const next = { ...previous };
                        delete next[serialNumber];
                        return next;
                    });
                    return;
                }

                setAgentCommandStatuses((previous) => ({
                    ...previous,
                    [serialNumber]: {
                        status: commandData.status,
                        exitCode: commandData.exitCode,
                        output: commandData.output,
                        completedAtUtc: commandData.completedAtUtc,
                    },
                }));

                if (commandData.status === 'pending' || commandData.status === 'running') {
                    pollAgentCommandStatus(serialNumber, commandData.id);
                }
            })
            .catch(() => {
                // Ignore reload errors; user can queue a new command.
            });
    }, [activeRegistration, pollAgentCommandStatus]);

    useEffect(() => {
        if (filteredRegistrations.length === 0) {
            setActiveSerialNumber(null);
            return;
        }

        if (activeSerialNumber && !filteredRegistrations.some((item) => item.serialNumber === activeSerialNumber)) {
            setActiveSerialNumber(null);
        }
    }, [filteredRegistrations, activeSerialNumber]);

    useEffect(() => {
        if (!activeRegistration) {
            return;
        }

        void loadDeviceDetailsForRegistration(activeRegistration);
    }, [activeRegistration, loadDeviceDetailsForRegistration]);

    const summaryMetrics = useMemo(() => {
        const reportedDevices = registrations.filter((item) => parseDateSafe(item.firstReportAt) > 0);
        const importedOnly = registrations.filter((item) => parseDateSafe(item.firstReportAt) === 0);
        const total = reportedDevices.length;
        const online = reportedDevices.filter((item) => isOnlineNow(item)).length;

        return {
            total,
            online,
            offline: Math.max(total - online, 0),
            importedOnly: importedOnly.length,
        };
    }, [registrations]);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <div className="dashboard">
            <div className="devices-sticky-top">
                <div className="devices-header-row">
                    <div>
                        <h2>Devices</h2>
                        <p className="summary">One overview for customers, registration data and actions.</p>
                    </div>
                    <div className="devices-header-actions">
                        {isOwner && (
                            <button
                                type="button"
                                className="primary-button"
                                onClick={openCreateRegistration}
                            >
                                New registration
                            </button>
                        )}
                        <button
                            type="button"
                            className="secondary-button"
                            onClick={loadAllDeviceData}
                            disabled={isLoadingVersionsBatch}
                        >
                            {isLoadingVersionsBatch ? 'Refreshing...' : 'Refresh'}
                        </button>
                    </div>
                </div>

                <div className="devices-summary-cards">
                    <div className="devices-summary-card devices-summary-card-total">
                        <span>Total</span>
                        <strong>{summaryMetrics.total}</strong>
                    </div>
                    <div className="devices-summary-card devices-summary-card-online">
                        <span>Online</span>
                        <strong>{summaryMetrics.online}</strong>
                    </div>
                    <div className="devices-summary-card devices-summary-card-offline">
                        <span>Offline</span>
                        <strong>{summaryMetrics.offline}</strong>
                    </div>
                    <div className="devices-summary-card devices-summary-card-imported">
                        <span>Imported only</span>
                        <strong>{summaryMetrics.importedOnly}</strong>
                    </div>
                </div>
            </div>

            <div className="devices-workspace">
                <section className="devices-list-panel" style={{ gridColumn: '1 / -1' }}>
                    <div className="devices-list-toolbar">
                        <input
                            className="search-input"
                            type="search"
                            placeholder="Search customer, serial, phone, host, city"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                        <label className="devices-toggle-label">
                            <input
                                type="checkbox"
                                checked={showImportedWithoutReports}
                                onChange={(event) => setShowImportedWithoutReports(event.target.checked)}
                            />
                            Show imported without reports
                        </label>
                        <span>{filteredRegistrations.length} results</span>
                    </div>
                    {!searchTerm.trim() && !showImportedWithoutReports && (
                        <p className="summary" style={{ marginBottom: '8px', fontSize: '0.9rem' }}>
                            Showing devices with at least one report. Use search to also find imported registrations without reports.
                        </p>
                    )}

                    <div className="devices-list-wrap">
                        <table className="devices-list-table">
                            <thead>
                                <tr>
                                    <th>Customer</th>
                                    <th>Serial</th>
                                    <th>Organ</th>
                                    <th>VPO</th>
                                    <th>Installer</th>
                                    <th>Latest report</th>
                                    <th>SystemReporter</th>
                                    <th>Agent</th>
                                    <th>Status</th>
                                    <th>Last seen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRegistrations.map((registration) => {
                                    const serialNumber = registration.serialNumber;
                                    const isActive = serialNumber === activeSerialNumber;
                                    const hasReport = parseDateSafe(registration.firstReportAt) > 0;
                                    const isOnline = isOnlineNow(registration);
                                    const effectiveLastSeenAt = getEffectiveLastSeenAt(registration);
                                    const statusClass = !hasReport
                                        ? 'devices-status-badge devices-status-badge-imported'
                                        : isOnline
                                            ? 'devices-status-badge devices-status-badge-online'
                                            : 'devices-status-badge devices-status-badge-offline';
                                    const statusLabel = !hasReport
                                        ? 'Imported'
                                        : isOnline
                                            ? 'Online'
                                            : 'Offline';
                                    const contentVersion = getDisplayContentVersion(contentVersionBySerial[serialNumber]);
                                    const systemReporterVersion = getDisplayVersion(systemReporterVersionBySerial[serialNumber]);
                                    const latestReportAt = latestReportAtBySerial[serialNumber];
                                    const isExpanded = expandedSerials.has(serialNumber);
                                    const latestInstallerVersion = installerReleaseNotes[0]?.version ?? '';
                                    const isInstallerOutdated = Boolean(contentVersion)
                                        && isValidSemanticVersion(contentVersion)
                                        && isValidSemanticVersion(latestInstallerVersion)
                                        && isVersionLowerThan(contentVersion, latestInstallerVersion);
                                    const isInstallerUpToDate = Boolean(contentVersion)
                                        && isValidSemanticVersion(contentVersion)
                                        && isValidSemanticVersion(latestInstallerVersion)
                                        && !isVersionLowerThan(contentVersion, latestInstallerVersion);
                                    return (
                                        <tr
                                            key={registration.id}
                                            className={[
                                                'devices-list-row',
                                                isActive ? 'active' : '',
                                                isExpanded ? 'expanded' : '',
                                            ].filter(Boolean).join(' ')}
                                            onClick={() => setActiveSerialNumber(serialNumber)}
                                        >
                                            <td data-label="Customer">
                                                <span>{registration.customer || '-'}</span>
                                                <button
                                                    type="button"
                                                    className="devices-row-expand"
                                                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedSerials((prev) => {
                                                            const next = new Set(prev);
                                                            if (next.has(serialNumber)) {
                                                                next.delete(serialNumber);
                                                            } else {
                                                                next.add(serialNumber);
                                                            }
                                                            return next;
                                                        });
                                                    }}
                                                >
                                                    {isExpanded ? '▲' : '▼'}
                                                </button>
                                            </td>
                                            <td data-label="Serial">{serialNumber}</td>
                                            <td data-label="Organ">{registration.organ || '-'}</td>
                                            <td data-label="VPO">{registration.vpo || '-'}</td>
                                            <td
                                                data-label="Installer"
                                                className={isInstallerOutdated ? 'devices-installer-outdated' : isInstallerUpToDate ? 'devices-installer-uptodate' : undefined}
                                                title={isInstallerOutdated ? `Newer installer available: ${latestInstallerVersion}` : isInstallerUpToDate ? 'Up to date with the latest installer' : undefined}
                                            >
                                                {contentVersion || '-'}
                                            </td>
                                            <td data-label="Latest report">{formatDateTimeLabel(latestReportAt)}</td>
                                            <td data-label="SystemReporter">{systemReporterVersion || '-'}</td>
                                            <td data-label="Agent">{formatAgentVersion(registration.agentVersion) || '-'}</td>
                                            <td data-label="Status"><span className={statusClass}>{statusLabel}</span></td>
                                            <td data-label="Last seen" title={effectiveLastSeenAt ? new Date(effectiveLastSeenAt).toLocaleString() : 'Never seen'}>
                                                {formatLastSeenLabel(effectiveLastSeenAt)}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredRegistrations.length === 0 && (
                                    <tr>
                                        <td colSpan={10} className="ticket-list-empty">No devices found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            {activeRegistration && (() => {
                const serialNumber = activeRegistration.serialNumber;
                const resolvedTeamViewerId = normalizeTeamViewerId(activeRegistration.teamViewerId)
                    || teamViewerIdsBySerial[serialNumber]
                    || '';
                const teamViewerUrl = resolvedTeamViewerId
                    ? `https://start.teamviewer.com/${encodeURIComponent(resolvedTeamViewerId)}`
                    : '';
                const isLoadingTeamViewer = loadingTeamViewerForSerial === serialNumber;
                const isLoadingLatestReport = loadingLatestReportForSerial === serialNumber;
                const isLoadingAgentCommand = agentCommandLoadingForSerial === serialNumber;
                const systemReporterVersion = getDisplayVersion(systemReporterVersionBySerial[serialNumber]);
                const installerVersion = getDisplayContentVersion(contentVersionBySerial[serialNumber]);
                const canRetrieveDiagnostics = Boolean(systemReporterVersion)
                    && isValidSemanticVersion(systemReporterVersion)
                    && !isVersionLowerThan(systemReporterVersion, MIN_DIAGNOSTICS_SYSTEMREPORTER_VERSION);
                const isLegacyInstaller = Boolean(installerVersion)
                    && isValidSemanticVersion(installerVersion)
                    && isVersionLowerThan(installerVersion, MIN_AGENT_COMMAND_INSTALLER_VERSION);
                const isSystemReporterAtLeast1067 = Boolean(systemReporterVersion)
                    && isValidSemanticVersion(systemReporterVersion)
                    && !isVersionLowerThan(systemReporterVersion, '1.0.67');
                const agentCommandDisabledReason = isLegacyInstaller && !isSystemReporterAtLeast1067
                    ? `Requires installer ${MIN_AGENT_COMMAND_INSTALLER_VERSION} or newer, or SystemReporter 1.0.67+`
                    : '';
                const isInstallerUpdateConfirmationOpen = installerUpdateConfirmationSerial === serialNumber;
                const activeEffectiveLastSeenAt = getEffectiveLastSeenAt(activeRegistration);
                const updateRiskReasons = getUpdateRiskReasons(activeEffectiveLastSeenAt, systemReporterVersion);
                const latestInstallerRelease = installerReleaseNotes[0]?.version ?? '';
                const installerReleasesForDevice = isValidSemanticVersion(installerVersion)
                    ? installerReleaseNotes.filter((release) => isVersionLowerThan(installerVersion, release.version))
                    : installerReleaseNotes.slice(0, 1);
                const hasInstallerReleaseNotes = installerReleasesForDevice.length > 0;

                return (
                    <div className="modal-backdrop" onClick={closeDeviceDetails}>
                        <div className="modal device-details-modal" onClick={(event) => event.stopPropagation()}>
                            <h3>Device details</h3>
                            {isOnlineNow(activeRegistration) && (
                                <span className="online-badge">Online</span>
                            )}
                            <div className="registration-meta">
                                <p className="registration-meta-row">
                                    <span className="registration-meta-label">Serial number:</span>
                                    <span className="registration-meta-value">{serialNumber}</span>
                                </p>
                                <p className="registration-meta-row">
                                    <span className="registration-meta-label">Name:</span>
                                    <span className="registration-meta-value">{activeRegistration.customer || '-'}</span>
                                </p>
                                <p className="registration-meta-row">
                                    <span className="registration-meta-label">Phone:</span>
                                    <span className="registration-meta-value">{activeRegistration.customerPhone || '-'}</span>
                                </p>
                                <p className="registration-meta-row">
                                    <span className="registration-meta-label">Email:</span>
                                    <span className="registration-meta-value">{activeRegistration.customerEmail || '-'}</span>
                                </p>
                                <p className="registration-meta-row">
                                    <span className="registration-meta-label">Hostname:</span>
                                    <span className="registration-meta-value">{activeRegistration.hostName || '-'}</span>
                                </p>
                                <p className="registration-meta-row">
                                    <span className="registration-meta-label">City:</span>
                                    <span className="registration-meta-value">{activeRegistration.clientCity || '-'}</span>
                                </p>
                                <p className="registration-meta-row">
                                    <span className="registration-meta-label">Last seen at:</span>
                                    <span className="registration-meta-value">{activeEffectiveLastSeenAt ? new Date(activeEffectiveLastSeenAt).toLocaleString() : '-'}</span>
                                </p>
                                <p className="registration-meta-row">
                                    <span className="registration-meta-label">Content agent:</span>
                                    <span className="registration-meta-value">{formatAgentVersion(activeRegistration.agentVersion) || '-'}</span>
                                </p>
                                <p className="registration-meta-row">
                                    <span className="registration-meta-label">Registered at:</span>
                                    <span className="registration-meta-value">{new Date(activeRegistration.registeredAt).toLocaleString()}</span>
                                </p>
                            </div>

                            <div className="installer-release-panel">
                                <p className="installer-release-title">What&apos;s new in installer updates</p>
                                <p className="installer-release-meta">
                                    Current: {installerVersion || 'unknown'} | Latest: {latestInstallerRelease || 'unknown'}
                                </p>
                                {hasInstallerReleaseNotes ? (
                                    <ul className="installer-release-list">
                                        {installerReleasesForDevice.map((release) => (
                                            <li key={release.version} className="installer-release-item">
                                                <p className="installer-release-version">
                                                    v{release.version}
                                                    <span>{new Date(release.releasedOn).toLocaleDateString('en-GB')}</span>
                                                </p>
                                                <ul className="installer-release-highlights">
                                                    {release.highlights.map((item) => (
                                                        <li key={item}>{item}</li>
                                                    ))}
                                                </ul>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="installer-release-up-to-date">This device is already on the latest installer release.</p>
                                )}
                            </div>

                            <div className="registration-actions">
                                <p className="registration-actions-title">Quick actions</p>
                                <button
                                    type="button"
                                    className="action-button action-notes"
                                    onClick={() => {
                                        void openRegistrationDetails(activeRegistration);
                                        closeDeviceDetails();
                                    }}
                                    title="Edit customer, phone, email and notes"
                                >
                                    Edit Registration
                                </button>
                                <button
                                    type="button"
                                    className="action-button action-report"
                                    onClick={() => openLatestReportForRegistration(activeRegistration)}
                                    title="Open reports for this device"
                                    disabled={isLoadingLatestReport}
                                >
                                    {isLoadingLatestReport ? 'Loading...' : 'Reports'}
                                </button>
                                <button
                                    type="button"
                                    className="action-button action-agent"
                                    onClick={() => openInstallerUpdateConfirmation(serialNumber)}
                                    title={agentCommandDisabledReason || 'Install the latest update package on this PC'}
                                    disabled={isLoadingAgentCommand || (isLegacyInstaller && !isSystemReporterAtLeast1067)}
                                >
                                    {isLoadingAgentCommand ? 'Queueing...' : 'Install Latest Update'}
                                </button>
                                {teamViewerUrl ? (
                                    <a
                                        className="action-button action-teamviewer"
                                        href={teamViewerUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Open TeamViewer in a new tab"
                                    >
                                        Open TeamViewer
                                    </a>
                                ) : (
                                    <button
                                        type="button"
                                        className="action-button action-teamviewer"
                                        onClick={() => void loadTeamViewerForRegistration(activeRegistration)}
                                        disabled={isLoadingTeamViewer}
                                        title="Load TeamViewer ID"
                                    >
                                        {isLoadingTeamViewer
                                            ? 'Preparing TeamViewer...'
                                            : hasNoTeamViewerIdError(serialNumber)
                                                ? 'No TeamViewer ID available'
                                                : 'Load TeamViewer'}
                                    </button>
                                )}
                                {isAdmin && (
                                    // Disabled buttons do not fire hover events, so the tooltip lives on the wrapper.
                                    <span
                                        className={`diagnostics-action-wrapper${canRetrieveDiagnostics ? '' : ' disabled'}`}
                                        title={canRetrieveDiagnostics
                                            ? 'Open diagnostics and logs in a new tab'
                                            : `Diagnostics unavailable: requires SystemReporter ${MIN_DIAGNOSTICS_SYSTEMREPORTER_VERSION} or newer `
                                                + `(current: ${systemReporterVersion || 'unknown'}). `
                                                + 'The device updates automatically on its next boot.'}
                                    >
                                        <button
                                            type="button"
                                            className="action-button action-report"
                                            disabled={!canRetrieveDiagnostics}
                                            onClick={() => {
                                                window.open(
                                                    `/diagnostics/${encodeURIComponent(serialNumber)}`,
                                                    '_blank',
                                                    'noopener,noreferrer'
                                                );
                                            }}
                                        >
                                            Diagnostics / logs
                                        </button>
                                    </span>
                                )}
                                <button
                                    type="button"
                                    className="action-button"
                                    onClick={closeDeviceDetails}
                                >
                                    Close
                                </button>
                                {isLegacyInstaller && !isSystemReporterAtLeast1067 && (
                                    <p className="registration-actions-hint">
                                        Install update requires installer {MIN_AGENT_COMMAND_INSTALLER_VERSION} or newer, or SystemReporter 1.0.67 or higher.<br />
                                        Current installer: {installerVersion}.<br />
                                        Current SystemReporter: {systemReporterVersion}.
                                    </p>
                                )}
                            </div>

                            {isInstallerUpdateConfirmationOpen && (
                                <div className="modal-backdrop" onClick={closeInstallerUpdateConfirmation}>
                                    <div className="modal confirm-modal" onClick={(event) => event.stopPropagation()}>
                                        <h4>Confirm update</h4>
                                        <p className="confirm-modal-message">
                                            Are you sure you want to update to the latest version? This update will be performed online on the customer's device.
                                        </p>
                                        {hasInstallerReleaseNotes && (
                                            <div className="confirm-release-notes">
                                                <p>Changes included in this update:</p>
                                                <ul>
                                                    {installerReleasesForDevice.slice(0, 3).map((release) => (
                                                        <li key={release.version}>
                                                            <strong>v{release.version}:</strong> {release.highlights.join(' ')}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        <div className="confirm-modal-actions">
                                            <button
                                                type="button"
                                                className="secondary-button"
                                                onClick={closeInstallerUpdateConfirmation}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="primary-button"
                                                onClick={() => void confirmInstallerUpdateCommand(activeRegistration)}
                                            >
                                                Yes, install latest update
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {agentCommandStatuses[serialNumber]?.status && (
                                <div className="agent-command-status-panel" style={{
                                    marginTop: '12px',
                                    padding: '12px',
                                    backgroundColor: agentCommandStatuses[serialNumber]?.status === 'succeeded' ? '#e8f5e9' : agentCommandStatuses[serialNumber]?.status === 'failed' ? '#ffebee' : '#e3f2fd',
                                    border: `1px solid ${agentCommandStatuses[serialNumber]?.status === 'succeeded' ? '#4caf50' : agentCommandStatuses[serialNumber]?.status === 'failed' ? '#f44336' : '#2196f3'}`,
                                    borderRadius: '4px'
                                }}>
                                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                                        Command Status: <span style={{
                                            color: agentCommandStatuses[serialNumber]?.status === 'succeeded' ? '#2e7d32' : agentCommandStatuses[serialNumber]?.status === 'failed' ? '#c62828' : '#1565c0'
                                        }}>
                                            {agentCommandStatuses[serialNumber]?.status}
                                        </span>
                                    </p>
                                    {agentCommandStatuses[serialNumber]?.exitCode !== undefined && agentCommandStatuses[serialNumber]?.status !== 'succeeded' && (
                                        <p style={{ margin: '0 0 8px 0' }}>
                                            Exit Code: {agentCommandStatuses[serialNumber]?.exitCode}
                                        </p>
                                    )}
                                    {agentCommandStatuses[serialNumber]?.output && (
                                        <div style={{
                                            backgroundColor: '#fff',
                                            padding: '8px',
                                            borderRadius: '2px',
                                            maxHeight: '200px',
                                            overflow: 'auto',
                                            fontFamily: 'monospace',
                                            fontSize: '12px',
                                            whiteSpace: 'pre-wrap',
                                            wordWrap: 'break-word'
                                        }}>
                                            {agentCommandStatuses[serialNumber]?.status === 'succeeded' && agentCommandStatuses[serialNumber]?.completedAtUtc
                                                ? `Latest update successfully installed at ${new Date(agentCommandStatuses[serialNumber].completedAtUtc).toLocaleString()}`
                                                : agentCommandStatuses[serialNumber]?.output}
                                        </div>
                                    )}
                                </div>
                            )}

                            {agentCommandErrors[serialNumber] && (
                                <div className="error-message" style={{
                                    marginTop: '12px',
                                    padding: '12px',
                                    backgroundColor: '#ffebee',
                                    color: '#c62828',
                                    borderRadius: '4px'
                                }}>
                                    {agentCommandErrors[serialNumber]}
                                </div>
                            )}

                            {updateRiskReasons.length > 0 && (
                                <div className="registration-warning-panel" role="alert">
                                    <p><strong>Possible reasons why self-update may fail:</strong></p>
                                    {systemReporterVersion && (
                                        <p><strong>SystemReporter version:</strong> {systemReporterVersion}</p>
                                    )}
                                    {!systemReporterVersion && (
                                        <p><strong>SystemReporter version:</strong> unknown</p>
                                    )}
                                    <ul className="registration-warning-list">
                                        {updateRiskReasons.map((reason) => (
                                            <li key={reason.code}>{reason.message}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {teamViewerErrors[serialNumber] && (
                                <p className="error-text registration-error">{teamViewerErrors[serialNumber]}</p>
                            )}
                            {latestReportErrors[serialNumber] && (
                                <p className="error-text registration-error">{latestReportErrors[serialNumber]}</p>
                            )}
                            {agentCommandErrors[serialNumber] && (
                                <p className="error-text registration-error">{agentCommandErrors[serialNumber]}</p>
                            )}
                            {agentCommandMessages[serialNumber] && (
                                <p className="success-text registration-error">{agentCommandMessages[serialNumber]}</p>
                            )}
                        </div>
                    </div>
                );
            })()}

            {selectedRegistration && (
                <div className="modal-backdrop" onClick={closeRegistrationDetails}>
                    <div className="modal" onClick={(event) => event.stopPropagation()}>
                        <h3>Edit registration</h3>
                        <p><strong>Serial number:</strong> {selectedRegistration.serialNumber}</p>
                        <label htmlFor="registration-customer" className="notes-label">Customer</label>
                        <input
                            id="registration-customer"
                            className="notes-textarea notes-textarea-readonly"
                            value={selectedRegistration.customer || ''}
                            readOnly
                            placeholder="Customer name"
                        />
                        <p className="registration-source-warning">
                            Customer name is read-only here. Source of truth is SystemReporter (organ-configurator) and the latest report value will be used automatically.
                        </p>
                        <label htmlFor="registration-customer-phone" className="notes-label">Customer phone</label>
                        <input
                            id="registration-customer-phone"
                            className="notes-textarea"
                            value={customerPhoneDraft}
                            onChange={(event) => setCustomerPhoneDraft(normalizeCustomerPhone(event.target.value))}
                            type="tel"
                            placeholder="Phone / WhatsApp"
                        />
                        <label htmlFor="registration-customer-email" className="notes-label">Customer email</label>
                        <input
                            id="registration-customer-email"
                            type="email"
                            className="notes-textarea"
                            value={customerEmailDraft}
                            onChange={(event) => setCustomerEmailDraft(event.target.value)}
                            placeholder="Email address"
                        />
                        <label htmlFor="registration-notes" className="notes-label">Notes</label>
                        <textarea
                            id="registration-notes"
                            className="notes-textarea"
                            value={notesDraft}
                            onChange={(event) => setNotesDraft(event.target.value)}
                            rows={8}
                            placeholder="Add notes here..."
                        />

                        {saveError && <p className="error-text">{saveError}</p>}

                        <div className="modal-actions">
                            <button type="button" className="secondary-button" onClick={closeRegistrationDetails} disabled={isLoadingDetails}>
                                Cancel
                            </button>
                            <button type="button" className="secondary-button" onClick={refreshRegistrationDetails} disabled={isLoadingDetails}>
                                {isLoadingDetails ? 'Refreshing...' : 'Refresh'}
                            </button>
                            <button type="button" className="primary-button" onClick={saveRegistration} disabled={isSaving || isLoadingDetails}>
                                {isSaving ? 'Saving...' : 'Save changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {isOwner && isCreateModalOpen && (
                <div className="modal-backdrop" onClick={closeCreateRegistration}>
                    <div className="modal" onClick={(event) => event.stopPropagation()}>
                        <h3>New registration</h3>
                        <label htmlFor="create-registration-serial" className="notes-label">Serial number</label>
                        <input
                            id="create-registration-serial"
                            className="notes-textarea"
                            value={createSerialDraft}
                            onChange={(event) => setCreateSerialDraft(event.target.value)}
                            placeholder="Serial number"
                        />
                        <label htmlFor="create-registration-customer" className="notes-label">Customer</label>
                        <input
                            id="create-registration-customer"
                            className="notes-textarea"
                            value={createCustomerDraft}
                            onChange={(event) => setCreateCustomerDraft(event.target.value)}
                            placeholder="Customer name"
                        />
                        <label htmlFor="create-registration-phone" className="notes-label">Customer phone</label>
                        <input
                            id="create-registration-phone"
                            className="notes-textarea"
                            value={createPhoneDraft}
                            onChange={(event) => setCreatePhoneDraft(event.target.value)}
                            placeholder="Phone / WhatsApp"
                        />
                        <label htmlFor="create-registration-email" className="notes-label">Customer email</label>
                        <input
                            id="create-registration-email"
                            type="email"
                            className="notes-textarea"
                            value={createEmailDraft}
                            onChange={(event) => setCreateEmailDraft(event.target.value)}
                            placeholder="Email address"
                        />
                        <label htmlFor="create-registration-notes" className="notes-label">Notes</label>
                        <textarea
                            id="create-registration-notes"
                            className="notes-textarea"
                            value={createNotesDraft}
                            onChange={(event) => setCreateNotesDraft(event.target.value)}
                            rows={5}
                            placeholder="Add notes here..."
                        />
                        <p className="registration-source-warning">
                            The MAC address is registered automatically on the first report from the device.
                        </p>

                        {createError && <p className="error-text">{createError}</p>}

                        <div className="modal-actions">
                            <button type="button" className="secondary-button" onClick={closeCreateRegistration} disabled={isCreating}>
                                Cancel
                            </button>
                            <button type="button" className="primary-button" onClick={submitCreateRegistration} disabled={isCreating}>
                                {isCreating ? 'Creating...' : 'Create registration'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Registrations;
